import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Zap, Sparkles, Ticket, User as UserIcon, Mail as MailIcon, Calendar, Bell, Trash2, LogOut, GraduationCap, Pencil, Check, X } from 'lucide-react';
import useAuthStore from '../../../store/authStore';
import api from '../../../services/api';

// Onglet Mes infos : profil, notifications email, espace formateur, actions, zone danger.
export default function CompteInfos({ onDeleteAccount }) {
    const location = useLocation();
    const { user, fetchProfile, logoutAllSessions } = useAuthStore();
    // Rectification du prénom (droit de rectification) : seul champ modifiable du profil.
    const [prenomEdit, setPrenomEdit] = useState(null); // null = lecture ; chaîne = en cours d'édition
    const [prenomBusy, setPrenomBusy] = useState(false);
    const [prenomErr, setPrenomErr] = useState('');
    const savePrenom = async () => {
        setPrenomBusy(true); setPrenomErr('');
        try {
            await api.post('/user/profile.php', { prenom: prenomEdit });
            await fetchProfile();
            setPrenomEdit(null);
        } catch (e) {
            setPrenomErr(e.response?.data?.message || 'Enregistrement impossible.');
        }
        setPrenomBusy(false);
    };
    const [notifPrefs, setNotifPrefs] = useState([]);
    const [notifLoading, setNotifLoading] = useState(true);
    const [notifSaving, setNotifSaving] = useState(null); // key in progress
    const [formateurSaving, setFormateurSaving] = useState(false);
    const [sessionsBusy, setSessionsBusy] = useState(false);
    const [sessionsMsg, setSessionsMsg] = useState(null); // { ok, text }

    useEffect(() => {
        api.get('/user/notifications.php')
            .then(({ data }) => setNotifPrefs(data.data || []))
            .catch(e => console.warn("Erreur notif prefs:", e))
            .finally(() => setNotifLoading(false));
        // Scroll to notifications block if URL has hash
        if (location.hash === '#notifications') {
            setTimeout(() => {
                document.getElementById('notifications')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 200);
        }
    }, []);

    const toggleNotif = async (key, currentValue) => {
        const newValue = currentValue === 1 ? 0 : 1;
        setNotifSaving(key);
        // Optimistic update
        setNotifPrefs(prev => prev.map(p => p.key === key ? { ...p, value: newValue } : p));
        try {
            await api.post('/user/notifications.php', { action: 'update', preferences: { [key]: newValue } });
        } catch {
            // Rollback
            setNotifPrefs(prev => prev.map(p => p.key === key ? { ...p, value: currentValue } : p));
        }
        setNotifSaving(null);
    };

    // Active / masque l'espace formateur (self-service depuis "Mes infos").
    // Masquer = is_formateur=0 sans rien supprimer (codes/suivis conservés).
    const toggleFormateur = async () => {
        setFormateurSaving(true);
        try {
            await api.post('/formateur/join.php', { enabled: !user.is_formateur });
            await fetchProfile();
        } catch { /* silencieux */ }
        setFormateurSaving(false);
    };

    const handleLogoutOthers = async () => {
        if (sessionsBusy) return;
        if (!window.confirm('Déconnecter toutes vos autres sessions (autres appareils et navigateurs) ? Cet appareil restera connecté.')) return;
        setSessionsBusy(true);
        setSessionsMsg(null);
        const res = await logoutAllSessions();
        setSessionsBusy(false);
        setSessionsMsg({ ok: !!res.success, text: res.success ? 'Toutes vos autres sessions ont été déconnectées.' : (res.message || 'Erreur') });
    };

    const infoRowStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 18px',
    };
    const labelStyle = {
        fontSize: '0.78em',
        color: 'var(--text-muted)',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        minWidth: 100,
    };
    const valueStyle = { fontSize: '1em', flex: 1, wordBreak: 'break-word' };
    const sectionTitleStyle = {
        fontSize: '0.78em',
        fontWeight: 600,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        margin: '24px 0 8px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    };

    return (
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
            {/* === SECTION : PROFIL === */}
            <h3 style={sectionTitleStyle}><UserIcon size={13} /> Mon profil</h3>
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={infoRowStyle}>
                    <UserIcon size={16} style={{ color: 'var(--text-muted)' }} />
                    <span style={labelStyle}>Prénom</span>
                    {prenomEdit === null ? (
                        <>
                            <span style={valueStyle}>{user.prenom || <em style={{ color: 'var(--text-muted)' }}>Non renseigné</em>}</span>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setPrenomEdit(user.prenom || ''); setPrenomErr(''); }}
                                aria-label="Modifier le prénom" title="Modifier le prénom"><Pencil size={14} /></button>
                        </>
                    ) : (
                        <form style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1, flexWrap: 'wrap' }}
                            onSubmit={(e) => { e.preventDefault(); if (prenomEdit.trim()) savePrenom(); }}>
                            <input type="text" value={prenomEdit} onChange={(e) => setPrenomEdit(e.target.value)} maxLength={50} autoFocus
                                disabled={prenomBusy} aria-label="Prénom"
                                style={{ flex: 1, minWidth: 140, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--input-bg, var(--card-bg))', color: 'var(--text)' }} />
                            <button type="submit" className="btn btn-primary btn-sm" disabled={prenomBusy || !prenomEdit.trim()} aria-label="Enregistrer"><Check size={14} /></button>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPrenomEdit(null)} disabled={prenomBusy} aria-label="Annuler"><X size={14} /></button>
                            {prenomErr && <span style={{ flexBasis: '100%', color: 'var(--danger, #dc2626)', fontSize: '0.82em' }}>{prenomErr}</span>}
                        </form>
                    )}
                </div>
                <div style={{ ...infoRowStyle, borderTop: '1px solid var(--border-soft, var(--border))' }}>
                    <MailIcon size={16} style={{ color: 'var(--text-muted)' }} />
                    <span style={labelStyle}>Email</span>
                    <span style={valueStyle}>{user.email}</span>
                </div>
                <div style={{ ...infoRowStyle, borderTop: '1px solid var(--border-soft, var(--border))' }}>
                    <Calendar size={16} style={{ color: 'var(--text-muted)' }} />
                    <span style={labelStyle}>Membre depuis</span>
                    <span style={valueStyle}>
                        {user.created_at
                            ? new Date(user.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                            : '—'}
                    </span>
                </div>
            </div>

            {/* === SECTION : NOTIFICATIONS === */}
            <h3 id="notifications" style={sectionTitleStyle}><Bell size={13} /> Notifications par email</h3>
            {notifLoading ? (
                <div style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '0.88em' }}>Chargement…</div>
            ) : notifPrefs.length === 0 ? (
                <div style={{ padding: '14px 18px', color: 'var(--text-muted)', fontSize: '0.88em', fontStyle: 'italic' }}>
                    Aucune préférence configurable pour le moment.
                </div>
            ) : (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                    {notifPrefs.map((pref, idx) => (
                        <div key={pref.key} style={{
                            padding: '14px 18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 14,
                            borderTop: idx > 0 ? '1px solid var(--border-soft, var(--border))' : 'none'
                        }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 500, fontSize: '0.95em' }}>{pref.label}</div>
                                {pref.help && <div style={{ color: 'var(--text-muted)', fontSize: '0.82em', marginTop: 2 }}>{pref.help}</div>}
                            </div>
                            <button
                                type="button"
                                onClick={() => toggleNotif(pref.key, pref.value)}
                                disabled={notifSaving === pref.key}
                                aria-pressed={pref.value === 1}
                                title={pref.value === 1 ? 'Désactiver' : 'Activer'}
                                style={{
                                    position: 'relative',
                                    width: 44,
                                    height: 24,
                                    borderRadius: 12,
                                    border: 'none',
                                    background: pref.value === 1 ? 'var(--primary, #7c3aed)' : 'var(--border, #cbd5e1)',
                                    cursor: notifSaving === pref.key ? 'wait' : 'pointer',
                                    transition: 'background 0.2s',
                                    flexShrink: 0,
                                    padding: 0,
                                    opacity: notifSaving === pref.key ? 0.6 : 1
                                }}
                            >
                                <span style={{
                                    position: 'absolute',
                                    top: 2,
                                    left: pref.value === 1 ? 22 : 2,
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    background: 'white',
                                    transition: 'left 0.2s',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                }} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* === SECTION : ESPACE FORMATEUR === */}
            <h3 style={sectionTitleStyle}><GraduationCap size={13} /> Espace formateur</h3>
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, fontSize: '0.95em' }}>Activer l'espace formateur</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.82em', marginTop: 2 }}>
                            Donnez accès à vos apprenants par code et suivez leurs résultats. Le désactiver masque seulement le menu — vos codes et suivis sont conservés.
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={toggleFormateur}
                        disabled={formateurSaving}
                        aria-pressed={!!user.is_formateur}
                        title={user.is_formateur ? 'Désactiver' : 'Activer'}
                        style={{
                            position: 'relative', width: 44, height: 24, borderRadius: 12, border: 'none',
                            background: user.is_formateur ? 'var(--primary, #7c3aed)' : 'var(--border, #cbd5e1)',
                            cursor: formateurSaving ? 'wait' : 'pointer', transition: 'background 0.2s', flexShrink: 0, padding: 0,
                            opacity: formateurSaving ? 0.6 : 1,
                        }}
                    >
                        <span style={{ position: 'absolute', top: 2, left: user.is_formateur ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </button>
                </div>
                {!!Number(user.is_formateur) && (
                    <Link
                        to="/formateur"
                        style={{ ...infoRowStyle, borderTop: '1px solid var(--border-soft, var(--border))', textDecoration: 'none', color: 'inherit', cursor: 'pointer', transition: 'background 0.15s' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,58,237,0.06)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                        <GraduationCap size={16} style={{ color: 'var(--primary, #7c3aed)' }} />
                        <span style={{ flex: 1, fontWeight: 500 }}>Ouvrir l'espace formateur</span>
                        <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                    </Link>
                )}
            </div>

            {/* === SECTION : ACTIONS === */}
            <h3 style={sectionTitleStyle}><Zap size={13} /> Actions</h3>
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                <Link
                    to="/compte/guide"
                    style={{
                        ...infoRowStyle,
                        textDecoration: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,58,237,0.06)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                    <Sparkles size={16} style={{ color: 'var(--primary, #7c3aed)' }} />
                    <span style={{ flex: 1, fontWeight: 500 }}>Revoir le guide de bienvenue</span>
                    <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                </Link>
                <Link
                    to="/compte/code"
                    style={{
                        ...infoRowStyle,
                        textDecoration: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        borderTop: '1px solid var(--border-soft, var(--border))',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,58,237,0.06)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                    <Ticket size={16} style={{ color: 'var(--primary, #7c3aed)' }} />
                    <span style={{ flex: 1, fontWeight: 500 }}>Activer un code</span>
                    <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                </Link>
                <button
                    type="button"
                    onClick={handleLogoutOthers}
                    disabled={sessionsBusy}
                    title="Invalide vos sessions sur les autres appareils et navigateurs ; cet appareil reste connecté."
                    style={{
                        ...infoRowStyle,
                        width: '100%',
                        background: 'transparent',
                        border: 'none',
                        borderTop: '1px solid var(--border-soft, var(--border))',
                        color: 'inherit',
                        cursor: sessionsBusy ? 'default' : 'pointer',
                        textAlign: 'left',
                        transition: 'background 0.15s',
                        font: 'inherit',
                    }}
                    onMouseEnter={(e) => { if (!sessionsBusy) e.currentTarget.style.background = 'rgba(124,58,237,0.06)'; }}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                    <LogOut size={16} style={{ color: 'var(--text-muted)' }} />
                    <span style={{ flex: 1, fontWeight: 500 }}>Déconnecter mes autres sessions</span>
                    <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>{sessionsBusy ? '…' : ''}</span>
                </button>
            </div>
            {sessionsMsg && (
                <p style={{ margin: '6px 2px 0', fontSize: '0.82em', color: sessionsMsg.ok ? '#16a34a' : '#ef4444' }}>{sessionsMsg.text}</p>
            )}

            {/* === SECTION : ZONE DANGER === */}
            <h3 style={{ ...sectionTitleStyle, color: '#ef4444' }}><Trash2 size={13} /> Zone de danger</h3>
            <div style={{ background: 'var(--card-bg)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, overflow: 'hidden' }}>
                <button
                    type="button"
                    onClick={onDeleteAccount}
                    style={{
                        ...infoRowStyle,
                        width: '100%',
                        background: 'transparent',
                        border: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background 0.15s',
                        font: 'inherit',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239,68,68,0.06)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                    <Trash2 size={16} style={{ color: '#ef4444' }} />
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, color: '#ef4444' }}>Supprimer mon compte</div>
                        <div style={{ fontSize: '0.82em', color: 'var(--text-muted)', marginTop: 2 }}>
                            Suppression définitive de ton compte et de tes données personnelles. Action irréversible.
                        </div>
                    </div>
                    <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                </button>
            </div>
        </div>
    );
}
